<template>
  <section class="invoice-documents-tab">
    <DocumentsTableCard
      :title="documentsTitle"
      :subtitle="documentsSubtitle"
      :rows="documentsRows"
      :loading="loading"
      :show-download-all="showDownloadAllButton"
      :show-validity-column="false"
      @download-all="handleDownloadAll"
      @open-create="openCreateModal"
      @download-item="handleDownloadItem"
      @edit-item="handleEditItem"
      @delete-item="handleDeleteItem"
    />

    <DocumentsCreateModal
      :show="showCreateModal"
      :mode="documentModalMode"
      :initial-data="editingDocument"
      :show-source-selector="true"
      :allow-url-source="true"
      :show-validity-fields="false"
      :show-comments-field="true"
      @close="showCreateModal = false"
      @submit="handleCreateDocument"
    />

    <ConfirmActionModal
      :show="isDeleteConfirmModalOpen"
      :title="documentsText.confirmDelete.title"
      :subtitle="documentsText.confirmDelete.subtitle"
      :message="deleteConfirmMessage"
      :confirm-text="documentsText.confirmDelete.confirm"
      :cancel-text="documentsText.confirmDelete.cancel"
      @cancel="closeDeleteConfirmModal"
      @confirm="confirmDeleteItem"
    />
  </section>
</template>

<script>
import DocumentsTableCard from '@/components/documents/DocumentsTableCard.vue';
import DocumentsCreateModal from '@/components/documents/DocumentsCreateModal.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import simpleNotesService from '@/services/simpleNotes';
import {
  extractApiErrorMessage,
  extractPayload,
} from '@/utils/dataNormalization';
import { downloadBlobFile } from '@/utils/fileDownload';
import { useDomainText } from '@/composables/text/useDomainText';
import {
  buildDocumentDownloadTimestamp,
  isPdfDocument,
  normalizeDocumentDownloadNameSegment,
  openDocumentInNewTab,
  resolveDocumentDownloadErrorMessage,
  resolveDocumentDownloadFileName,
  resolveDocumentFileName,
  resolveDocumentUrl,
  triggerDocumentDirectDownload,
} from '@/composables/documents/useDocumentsDownload';
import {
  closeDocumentDeleteModal,
  confirmDocumentDelete,
  openDocumentCreateModal,
  openDocumentDeleteModal,
  openDocumentEditModal,
  resetDocumentModalState,
  submitDocumentCrud,
} from '@/composables/documents/useDocumentsCrud';

const { catalog: documentsText } = useDomainText('documents');
const { catalog: simpleNotesText } = useDomainText('simpleNotes');

export default {
  name: 'SimpleNoteDetailTabDocuments',
  components: {
    DocumentsTableCard,
    DocumentsCreateModal,
    ConfirmActionModal,
  },
  props: {
    note: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      documentsText,
      simpleNotesText,
      loading: false,
      documentsRows: [],
      showCreateModal: false,
      documentModalMode: 'create',
      editingDocument: null,
      isDeleteConfirmModalOpen: false,
      pendingDeleteDocument: null,
      deletingDocument: false,
    };
  },
  computed: {
    documentsTitle() {
      return String(this.simpleNotesText?.detail?.tabs?.documents || 'Documentos').trim() || 'Documentos';
    },
    documentsSubtitle() {
      return String(this.simpleNotesText?.detail?.tabPanels?.documents?.subtitle
        || 'Listado de archivos y documentos relacionados con esta nota simple.').trim()
        || 'Listado de archivos y documentos relacionados con esta nota simple.';
    },
    noteId() {
      return Number(this.note?.id || 0);
    },
    deleteConfirmMessage() {
      const name = String(this.pendingDeleteDocument?.name || '').trim();
      return name !== ''
        ? this.documentsText.confirmDelete.messageByName(name)
        : this.documentsText.confirmDelete.messageDefault;
    },
    showDownloadAllButton() {
      return this.documentsRows.some((row) => Number(row?.isFile || 0) === 1 && String(row?.documentRaw || '').trim() !== '');
    },
    noteDownloadNameSegment() {
      const noteName = String(this.note?.serial || '').trim() + String(this.note?.folio || '').trim();
      return normalizeDocumentDownloadNameSegment(noteName, 'NOTA_SIMPLE');
    },
  },
  watch: {
    noteId: {
      immediate: true,
      handler() {
        this.loadDocuments();
      },
    },
    showCreateModal(visible) {
      if (visible) {
        return;
      }

      resetDocumentModalState(this);
    },
  },
  methods: {
    mapDocumentRow(row) {
      return {
        id: Number(row?.id || 0),
        name: String(row?.name || this.documentsText.common.blankValue).trim() || this.documentsText.common.blankValue,
        validityStart: this.documentsText.common.blankValue,
        validityEnd: this.documentsText.common.blankValue,
        comments: String(row?.description || '').trim(),
        isFile: Number(row?.is_file || 0) === 1 ? 1 : 0,
        sourceOrigin: String(row?.source_origin || 'legacy').trim().toLowerCase() || 'legacy',
        documentRaw: String(row?.document || '').trim(),
        documentUrl: String(row?.document_url || '').trim(),
      };
    },
    openCreateModal() {
      openDocumentCreateModal(this);
    },
    async loadDocuments() {
      const noteId = this.noteId;
      if (noteId <= 0) {
        this.documentsRows = [];
        return;
      }

      this.loading = true;
      try {
        const response = await simpleNotesService.documentsIndex(noteId);
        const payload = extractPayload(response);
        const documents = Array.isArray(payload?.documents) ? payload.documents : [];
        this.documentsRows = documents.map((row) => this.mapDocumentRow(row));
      } catch (error) {
        this.documentsRows = [];
        this.$toast?.error(extractApiErrorMessage(error, 'No fue posible cargar los documentos de la nota simple.'));
      } finally {
        this.loading = false;
      }
    },
    async handleDownloadAll() {
      if (this.noteId <= 0) {
        return;
      }

      try {
        const response = await simpleNotesService.documentsDownloadAll(this.noteId);

        const fileName = resolveDocumentDownloadFileName(
          response,
          `documentos_nota_simple_${this.noteDownloadNameSegment}_${buildDocumentDownloadTimestamp()}.zip`
        );
        downloadBlobFile(response, fileName, 'application/zip');
        this.$toast?.success(this.documentsText.toasts.downloadStarted);
      } catch (error) {
        const message = await resolveDocumentDownloadErrorMessage(error, this.documentsText.toasts.noDocumentsToDownload);
        this.$toast?.error(message);
      }
    },
    async handleDownloadItem(row) {
      const isLegacyFile = Number(row?.isFile || 0) === 1 && String(row?.sourceOrigin || '').trim().toLowerCase() === 'legacy';
      if (isLegacyFile) {
        const documentId = Number(row?.id || 0);
        if (this.noteId <= 0 || documentId <= 0) {
          this.$toast?.info(this.documentsText.toasts.noDownloadableFile);
          return;
        }

        try {
          const response = await simpleNotesService.documentsDownload(this.noteId, documentId);
          const fileName = resolveDocumentDownloadFileName(response, resolveDocumentFileName(row));
          downloadBlobFile(response, fileName, 'application/octet-stream');
          return;
        } catch (error) {
          const message = await resolveDocumentDownloadErrorMessage(error, this.documentsText.toasts.noDownloadableFile);
          this.$toast?.error(message);
          return;
        }
      }

      const url = resolveDocumentUrl(row);
      if (url === '') {
        this.$toast?.info(this.documentsText.toasts.noDownloadableFile);
        return;
      }

      if (isPdfDocument(row)) {
        openDocumentInNewTab(url);
        return;
      }

      await triggerDocumentDirectDownload(url, resolveDocumentFileName(row));
    },
    handleEditItem(row) {
      openDocumentEditModal(this, row);
    },
    handleDeleteItem(row) {
      openDocumentDeleteModal(this, row);
    },
    closeDeleteConfirmModal() {
      closeDocumentDeleteModal(this);
    },
    async confirmDeleteItem() {
      const documentId = Number(this.pendingDeleteDocument?.id || 0);
      if (this.noteId <= 0 || documentId <= 0) {
        this.closeDeleteConfirmModal();
        return;
      }

      this.deletingDocument = true;
      try {
        const result = await confirmDocumentDelete({
          parentId: this.noteId,
          documentId,
          deleteRequest: (currentNoteId, currentDocumentId) => simpleNotesService.documentsDelete(currentNoteId, currentDocumentId),
          toast: this.$toast,
          successMessage: this.documentsText.toasts.deleteSuccess,
          errorMessage: this.documentsText.toasts.deleteError,
        });

        if (!result.ok) {
          return;
        }

        this.closeDeleteConfirmModal();
        await this.loadDocuments();
      } finally {
        this.deletingDocument = false;
      }
    },
    async handleCreateDocument(payload) {
      if (this.noteId <= 0) {
        return;
      }

      const result = await submitDocumentCrud({
        mode: this.documentModalMode,
        editingDocument: this.editingDocument,
        payload: {
          ...payload,
          validityStart: '',
          validityEnd: '',
        },
        updateRequest: (documentId, updatePayload) => simpleNotesService.documentsUpdate(this.noteId, documentId, updatePayload),
        createRequest: (formData, config) => simpleNotesService.documentsStore(this.noteId, formData, config),
        toast: this.$toast,
        toasts: this.documentsText.toasts,
      });

      if (!result.ok) {
        return;
      }

      try {
        this.showCreateModal = false;
        resetDocumentModalState(this);
        await this.loadDocuments();
      } catch (error) {
        this.$toast?.error(extractApiErrorMessage(error, 'No fue posible cargar los documentos de la nota simple.'));
      }
    },
  },
};
</script>

<style scoped>
.invoice-documents-tab {
  width: 100%;
}
</style>
